/**
 * Sticky cart bar.
 *
 * Shows the bottom bar while the cart has items and hides it when it empties.
 * The bar's contents are a WooCommerce fragment (inc/sticky-cart-bar.php), so
 * after every cart change the fresh `data-cart-count` on the fragment root is
 * the source of truth; this module only mirrors it onto the shell and body.
 *
 * `has-sticky-cart-bar` on <body> reserves room at the bottom of the page so
 * the footer is never stuck behind the bar. It is also set server-side, so a
 * page loaded with a full cart does not jump.
 *
 * Guarded on the markup: with the Main Options toggle off, or on the cart and
 * checkout pages, the bar is not rendered and nothing below runs.
 */

{
  const bar = document.querySelector<HTMLElement>(".niamito-sticky-cart-bar");

  if (bar) {
    const $ = (window as any).jQuery;
    let hideTimer: number | undefined;

    const show = (): void => {
      if (hideTimer !== undefined) {
        window.clearTimeout(hideTimer);
        hideTimer = undefined;
      }
      document.body.classList.add("has-sticky-cart-bar");
      if (bar.classList.contains("is-visible")) return;

      // `hidden` is dropped a frame before `is-visible` goes on so the slide-in
      // has a start state to animate from.
      bar.hidden = false;
      window.requestAnimationFrame(() => bar.classList.add("is-visible"));
    };

    const hide = (): void => {
      document.body.classList.remove("has-sticky-cart-bar");
      if (!bar.classList.contains("is-visible")) return;

      bar.classList.remove("is-visible");
      // Matches the transition in _sticky-cart-bar.css; `hidden` then takes the
      // links out of the tab order.
      hideTimer = window.setTimeout(() => {
        bar.hidden = true;
        hideTimer = undefined;
      }, 300);
    };

    const sync = (): void => {
      const contents = bar.querySelector<HTMLElement>(".niamito-sticky-cart-bar__contents");
      const count = parseInt(contents?.getAttribute("data-cart-count") ?? "0", 10);
      if (count > 0) {
        show();
      } else {
        hide();
      }
    };

    if ($) {
      // Fragment events cover adds, removals and cross-tab syncing; the add
      // events are listed too because the single product page fires its own
      // namespaced one (singleProductAddToCart.ts).
      $(document.body).on(
        "added_to_cart niamito_added_to_cart removed_from_cart wc_fragments_refreshed wc_fragments_loaded",
        sync
      );
    }

    // Fragments may already have been applied from the session cache before
    // this ran, leaving the markup ahead of the server-rendered state.
    sync();
  }
}
